import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { notifyDiscussionReply } from "@/lib/notifyDiscussionReply";

export interface GeneralDiscussion {
  id: string;
  user_id: string;
  title: string;
  content: string;
  image_url: string | null;
  link_url: string | null;
  is_pinned: boolean | null;
  created_at: string;
  updated_at: string;
  author?: {
    full_name: string;
    avatar_url: string | null;
  };
  reply_count?: number;
}

export interface GeneralDiscussionReply {
  id: string;
  discussion_id: string;
  user_id: string;
  content: string;
  image_url: string | null;
  link_url: string | null;
  created_at: string;
  updated_at: string;
  author?: {
    full_name: string;
    avatar_url: string | null;
  };
}

export function useGeneralDiscussions() {
  return useQuery({
    queryKey: ["general-discussions"],
    queryFn: async () => {
      const { data: discussions, error } = await supabase
        .from("general_discussions")
        .select("*")
        .order("is_pinned", { ascending: false })
        .order("created_at", { ascending: false });

      if (error) throw error;

      const userIds = [...new Set(discussions.map((d) => d.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .in("id", userIds);

      const discussionIds = discussions.map((d) => d.id);
      const { data: replyCounts } = await supabase
        .from("general_discussion_replies")
        .select("discussion_id")
        .in("discussion_id", discussionIds);

      const replyCountMap: Record<string, number> = {};
      replyCounts?.forEach((r) => {
        replyCountMap[r.discussion_id] = (replyCountMap[r.discussion_id] || 0) + 1;
      });

      return discussions.map((discussion) => ({
        ...discussion,
        author: profiles?.find((p) => p.id === discussion.user_id),
        reply_count: replyCountMap[discussion.id] || 0,
      })) as GeneralDiscussion[];
    },
  });
}

export function useGeneralDiscussion(discussionId: string) {
  return useQuery({
    queryKey: ["general-discussion", discussionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("general_discussions")
        .select("*")
        .eq("id", discussionId)
        .single();

      if (error) throw error;

      const { data: profile } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .eq("id", data.user_id)
        .single();

      return {
        ...data,
        author: profile,
      } as GeneralDiscussion;
    },
    enabled: !!discussionId,
  });
}

export function useGeneralDiscussionReplies(discussionId: string) {
  return useQuery({
    queryKey: ["general-discussion-replies", discussionId],
    queryFn: async () => {
      const { data: replies, error } = await supabase
        .from("general_discussion_replies")
        .select("*")
        .eq("discussion_id", discussionId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      const userIds = [...new Set(replies.map((r) => r.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .in("id", userIds);

      return replies.map((reply) => ({
        ...reply,
        author: profiles?.find((p) => p.id === reply.user_id),
      })) as GeneralDiscussionReply[];
    },
    enabled: !!discussionId,
  });
}

export function useCreateGeneralDiscussion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      title,
      content,
      userId,
      image_url,
      link_url,
    }: {
      title: string;
      content: string;
      userId: string;
      image_url?: string;
      link_url?: string;
    }) => {
      const { data, error } = await supabase
        .from("general_discussions")
        .insert({
          title,
          content,
          user_id: userId,
          image_url: image_url || null,
          link_url: link_url || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["general-discussions"] });
    },
  });
}

export function useCreateGeneralReply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      discussionId,
      content,
      userId,
      image_url,
      link_url,
      discussionTitle,
    }: {
      discussionId: string;
      content: string;
      userId: string;
      image_url?: string;
      link_url?: string;
      discussionTitle?: string;
    }) => {
      const { data, error } = await supabase
        .from("general_discussion_replies")
        .insert({
          discussion_id: discussionId,
          content,
          user_id: userId,
          image_url: image_url || null,
          link_url: link_url || null,
        })
        .select()
        .single();

      if (error) throw error;

      if (discussionTitle) {
        notifyDiscussionReply({
          discussionId,
          discussionType: "general",
          replyContent: content,
          replyAuthorId: userId,
          discussionTitle,
        });
      }

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["general-discussion-replies", variables.discussionId],
      });
      queryClient.invalidateQueries({ queryKey: ["general-discussions"] });
    },
  });
}

export function useUpdateGeneralDiscussion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      title,
      content,
      image_url,
      link_url,
    }: {
      id: string;
      title?: string;
      content: string;
      image_url?: string | null;
      link_url?: string | null;
    }) => {
      const { error } = await supabase
        .from("general_discussions")
        .update({
          ...(title !== undefined ? { title } : {}),
          content,
          image_url: image_url ?? null,
          link_url: link_url ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["general-discussion", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["general-discussions"] });
    },
  });
}

export function useUpdateGeneralReply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      content,
      image_url,
      link_url,
      discussionId,
    }: {
      id: string;
      content: string;
      image_url?: string | null;
      link_url?: string | null;
      discussionId: string;
    }) => {
      const { error } = await supabase
        .from("general_discussion_replies")
        .update({
          content,
          image_url: image_url ?? null,
          link_url: link_url ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["general-discussion-replies", variables.discussionId],
      });
    },
  });
}

export function useDeleteGeneralDiscussion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (discussionId: string) => {
      const { error } = await supabase
        .from("general_discussions")
        .delete()
        .eq("id", discussionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["general-discussions"] });
    },
  });
}

export function useDeleteGeneralReply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (replyId: string) => {
      const { error } = await supabase
        .from("general_discussion_replies")
        .delete()
        .eq("id", replyId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["general-discussion-replies"] });
      queryClient.invalidateQueries({ queryKey: ["general-discussions"] });
    },
  });
}

export function useToggleGeneralDiscussionPin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      discussionId,
      isPinned,
    }: {
      discussionId: string;
      isPinned: boolean;
    }) => {
      const { error } = await supabase
        .from("general_discussions")
        .update({ is_pinned: isPinned })
        .eq("id", discussionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["general-discussions"] });
    },
  });
}
